import { ExecutionJobService, type ExecutionJob, type RunTarget } from '../../services/execution-job.service';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, Subscription, switchMap, take, takeWhile, throwError, timer } from 'rxjs';
import type { CreatedTestRun, CreateTestRunRequest } from '../../models/test-run.model';
import { ReleaseService } from '../../services/release.service';
import { TestModuleService } from '../../services/test-module.service';
import { TestRunService } from '../../services/test-run.service';
import { createdRunRoute } from '../../shared/test-run-routes';
import { RunParametersSectionComponent } from '../run-parameters-section/run-parameters-section.component';
import { RunnerConnectHelpComponent } from '../runner-connect-help/runner-connect-help.component';
import { NACHLADEN_ABSTAND_MS, NACHLADEN_VERSUCHE } from '../runner-connect-help/runner-pairing';
import { RunnerRadioListComponent, type RunnerAvailability } from '../runner-radio-list/runner-radio-list.component';

export type RunMode = 'MANUAL' | 'LOCAL_AUTOMATED';

/** In der Reihenfolge der Knoepfe im Template. */
const RUN_MODES: readonly RunMode[] = ['MANUAL', 'LOCAL_AUTOMATED'];

const ARROW_STEP: Readonly<Record<string, number>> = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };

/**
 * Umgebung des manuellen Probelaufs. Der Dialog fragt nicht nach, und Staging ist die Umgebung,
 * in der geprueft wird; das Backend gleicht den Namen nicht mit dem Projekt ab (NEX-2283).
 */
const MANUAL_RUN_ENVIRONMENT = 'Staging';

/** Texte und Testkennungen je Gegenstand; der Testfall behaelt die aus NEX-2412. */
const CASE_TEXTS = {
  title: 'testCases.run.title',
  trialHint: 'testCases.runDialog.trialHint',
  dialogTestId: 'tf-ausfuehren-dialog',
  startTestId: 'run-start',
  errorTestId: 'run-start-error',
} as const;

const MODULE_TEXTS = {
  title: 'modules.run.title',
  trialHint: 'modules.run.trialHint',
  dialogTestId: 'module-run-dialog',
  startTestId: 'module-run-start',
  errorTestId: 'module-run-error',
} as const;

/** Was ausgefuehrt wird: ein Testfall oder ein Modul (NEX-2433). */
export interface RunSubject {
  kind: 'CASE' | 'MODULE';
  id: string;
  key: string | null;
  title: string;
}

export interface RunTestCaseDialogData {
  projectId: string;
  subject: RunSubject;
  hasUnsavedChanges: boolean;
  /** Zustaendig fuer den manuellen Probelauf; nur gesetzt, wenn der Startende zuweisbar ist. */
  owner?: string | null;
}

/**
 * `undefined` heisst abgebrochen. Manuell hat der Dialog den Lauf schon angelegt und ist hineingesprungen,
 * automatisiert den Auftrag eingereiht; verfolgt wird er beim Aufrufer.
 */
export type RunTestCaseDialogResult =
  | { kind: 'MANUAL'; run: CreatedTestRun }
  | { kind: 'AUTOMATED'; job: ExecutionJob }
  | { kind: 'MODULE_JOB'; jobId: string };

type StartErrorKey = 'forbidden' | 'notFound' | 'noExecutableStep' | 'generic';

/** Ein Text des Servers geht vor; sonst ein Schluessel mit Platzhaltern. */
type StartError = { text: string } | { key: string; params?: Record<string, string> };

/** Material setzt `aria-modal` nur auf Wunsch; der Dialog ist modal und sagt es (AK18). */
export function openRunTestCaseDialog(
  dialog: MatDialog,
  data: RunTestCaseDialogData,
): MatDialogRef<RunTestCaseDialogComponent, RunTestCaseDialogResult> {
  return dialog.open<RunTestCaseDialogComponent, RunTestCaseDialogData, RunTestCaseDialogResult>(
    RunTestCaseDialogComponent,
    { data, autoFocus: 'dialog', ariaModal: true },
  );
}

/**
 * Startet einen Testfall (NEX-2412) oder ein Modul (NEX-2437) einzeln, immer als Probelauf.
 *
 * <p>Manuell legt der Dialog den Probelauf selbst an und springt in den Runner. Automatisiert
 * reiht er den Auftrag ein; der Testlauf entsteht als Probelauf, wenn ein Rechner ihn abholt.
 */
@Component({
  selector: 'app-run-test-case-dialog',
  imports: [
    MatButton,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    MatIcon,
    MatProgressSpinner,
    RunParametersSectionComponent,
    RunnerConnectHelpComponent,
    RunnerRadioListComponent,
    TranslatePipe,
  ],
  templateUrl: './run-test-case-dialog.component.html',
  styleUrl: './run-test-case-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RunTestCaseDialogComponent {
  private static nextId = 0;

  private readonly dialogRef = inject(MatDialogRef<RunTestCaseDialogComponent, RunTestCaseDialogResult>);
  private readonly releases = inject(ReleaseService);
  private readonly executionJobs = inject(ExecutionJobService);
  private readonly testRuns = inject(TestRunService);
  private readonly testModules = inject(TestModuleService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  readonly data = inject<RunTestCaseDialogData>(MAT_DIALOG_DATA);
  readonly isModule = this.data.subject.kind === 'MODULE';
  readonly texts = this.isModule ? MODULE_TEXTS : CASE_TEXTS;

  readonly modeLabelId = `run-mode-label-${RunTestCaseDialogComponent.nextId}`;
  readonly connectHelpId = `run-connect-help-${RunTestCaseDialogComponent.nextId++}`;

  readonly selected = signal<RunMode>('MANUAL');

  /**
   * Ausfuehrbare Schritte des gespeicherten Testfalls laut Backend, Module aufgeloest (NEX-1504).
   * `null`, solange die Antwort aussteht. Ungespeicherte Aenderungen zaehlen bewusst nicht: Laufen
   * wuerde ohnehin der gespeicherte Stand.
   */
  readonly stepCount = signal<number | null>(null);
  readonly stepCountFailed = signal(false);

  /**
   * Laden, Fehler und 0 sperren gleich: Automatisiert startet nur, was sicher Schritte hat. Fuer ein
   * Modul gibt es die Zaehlung nicht; ohne Schritt lehnt der Server das Einreihen mit 422 ab.
   */
  readonly localAvailable = computed(() => this.isModule || (this.stepCount() ?? 0) > 0);

  private userChose = false;

  /** Rechner oder Anforderung aus der Auswahl; `null`, solange keins feststeht. */
  readonly target = signal<RunTarget | null>(null);

  private readonly runnerList = viewChild(RunnerRadioListComponent);
  private readonly parameters = viewChild(RunParametersSectionComponent);
  private readonly connectToggle = viewChild<ElementRef<HTMLButtonElement>>('connectToggle');

  /** `null`, bis die Rechnerliste zum ersten Mal geladen ist. */
  readonly availability = signal<RunnerAvailability | null>(null);

  /**
   * Ohne Rechner online klappt die Hilfe von selbst auf. Zu klappt sie nur, wer sie schliesst:
   * Nach dem Verbinden steht dort die Erfolgsmeldung.
   */
  readonly helpOpen = signal(false);
  readonly helpClosable = computed(() => this.availability() === 'someOnline');

  /** Name des gerade verbundenen Rechners; die Liste waehlt ihn, sobald er online ist. */
  readonly connectedRunner = signal<string | null>(null);
  private waitingForRunner?: Subscription;

  readonly starting = signal(false);
  readonly startError = signal<StartError | null>(null);

  /**
   * Gestartet wird automatisiert erst, wenn feststeht, auf welchem Rechner. Ein Modul geht nur an
   * einen bestimmten Rechner, nie an eine Etiketten-Anforderung.
   */
  readonly startDisabled = computed(() => {
    if (this.starting()) {
      return true;
    }
    if (this.isModule && !this.parameters()?.ready()) {
      return true;
    }
    if (this.selected() !== 'LOCAL_AUTOMATED') {
      return false;
    }
    const target = this.target();
    return target === null || (this.isModule && target.kind !== 'runner');
  });

  constructor() {
    if (this.isModule) {
      return;
    }
    if (!this.data.subject.id) {
      this.stepCount.set(0);
      return;
    }
    this.executionJobs
      .executableStepCount(this.data.projectId, this.data.subject.id)
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (count) => {
          this.stepCount.set(count);
          if (count > 0 && !this.userChose) {
            this.selected.set('LOCAL_AUTOMATED');
          }
        },
        error: () => this.stepCountFailed.set(true),
      });
  }

  select(mode: RunMode): void {
    this.userChose = true;
    this.selected.set(mode);
  }

  private readonly optionButtons = viewChildren<ElementRef<HTMLButtonElement>>('runOption');

  /**
   * Pfeiltasten wie in einer nativen Radiogruppe: Sie waehlen und fokussieren die naechste
   * Option, am Ende geht es vorne weiter. Gesperrte Optionen werden uebersprungen.
   */
  onOptionKeydown(event: KeyboardEvent): void {
    const step = ARROW_STEP[event.key];
    if (!step) {
      return;
    }
    event.preventDefault();
    const enabled = RUN_MODES.filter((mode) => this.isSelectable(mode));
    const next = enabled[(enabled.indexOf(this.selected()) + step + enabled.length) % enabled.length];
    this.select(next);
    this.optionButtons()[RUN_MODES.indexOf(next)]?.nativeElement.focus();
  }

  private isSelectable(mode: RunMode): boolean {
    return mode === 'MANUAL' || this.localAvailable();
  }

  start(): void {
    if (this.starting()) {
      return;
    }
    if (this.selected() === 'MANUAL') {
      this.startManualTrial();
      return;
    }
    const target = this.target();
    if (target) {
      this.startAutomatedTrial(target);
    }
  }

  onAvailability(availability: RunnerAvailability): void {
    this.availability.set(availability);
    if (availability !== 'someOnline') {
      this.helpOpen.set(true);
    }
  }

  toggleHelp(): void {
    this.helpOpen.update((open) => !open);
  }

  closeHelp(): void {
    this.helpOpen.set(false);
    this.connectToggle()?.nativeElement.focus();
  }

  /** Der Rechner entsteht erst, wenn der Runner den bestaetigten Code abholt; bis dahin wird nachgeladen. */
  onConnected(runnerName: string): void {
    this.connectedRunner.set(runnerName);
    this.waitingForRunner?.unsubscribe();
    this.waitingForRunner = timer(0, NACHLADEN_ABSTAND_MS)
      .pipe(
        take(NACHLADEN_VERSUCHE),
        takeWhile(() => !this.isChosen(runnerName)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.runnerList()?.reload());
  }

  private isChosen(runnerName: string): boolean {
    const target = this.target();
    return target?.kind === 'runner' && target.runnerName === runnerName;
  }

  /** Auch beim Sprung auf die Kontoseite: Sonst laege der Dialog ueber der Zielseite. */
  cancel(): void {
    this.dialogRef.close();
  }

  /**
   * Ein Release wird nur zugeordnet, wenn es „In Abnahme“ ist; sonst laeuft der Probelauf ohne.
   * Laesst sich die Liste nicht laden, startet nichts: Offen waere sonst, ob eins zuzuordnen war.
   */
  private startManualTrial(): void {
    const { projectId, subject, owner } = this.data;
    this.setStarting(true);
    this.startError.set(null);
    this.releases
      .currentRelease(projectId)
      .pipe(
        catchError(() => throwError(() => 'generic' as const)),
        switchMap((release) => {
          const common = {
            environment: MANUAL_RUN_ENVIRONMENT,
            ...(release?.status === 'IN_ABNAHME' ? { releaseId: release.id } : {}),
            ...(owner ? { owner } : {}),
          };
          if (this.isModule) {
            return this.testRuns.startModuleTrial(projectId, {
              ...common,
              testModuleId: subject.id,
              ...this.parameterValues(),
            });
          }
          const request: CreateTestRunRequest = {
            kind: 'MANUAL',
            sourceType: 'CASES',
            caseIds: [subject.id],
            trial: true,
            ...common,
          };
          return this.testRuns.create(projectId, request);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (created) => {
          const run = created[0];
          if (!run) {
            this.fail('generic');
            return;
          }
          this.dialogRef.close({ kind: 'MANUAL', run });
          void this.router.navigate(createdRunRoute(projectId, run));
        },
        error: (error: unknown) =>
          this.isModule ? this.failWith(moduleStartError(error, null)) : this.fail(startErrorKey(error)),
      });
  }

  private startAutomatedTrial(target: RunTarget): void {
    this.setStarting(true);
    this.startError.set(null);
    if (this.isModule) {
      this.startModuleJob(target);
      return;
    }
    this.executionJobs
      .enqueue(this.data.projectId, this.data.subject.id, target)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (job) => this.dialogRef.close({ kind: 'AUTOMATED', job }),
        error: (error: unknown) => {
          this.startError.set(enqueueError(error, target));
          this.setStarting(false);
          if (isRunnerOffline(error, target)) {
            this.runnerList()?.reload();
          }
        },
      });
  }

  private startModuleJob(target: RunTarget): void {
    if (target.kind !== 'runner') {
      this.setStarting(false);
      return;
    }
    this.testModules
      .enqueueModuleJob(
        this.data.projectId,
        this.data.subject.id,
        target.runnerId,
        this.parameterValues().parameterValues,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (jobId) => this.dialogRef.close({ kind: 'MODULE_JOB', jobId }),
        error: (error: unknown) => {
          this.failWith(moduleStartError(error, target));
          if (isRunnerOffline(error, target)) {
            this.runnerList()?.reload();
          }
        },
      });
  }

  /** Ohne Eingaben bleibt das Feld weg; dann kommt alles aus Datenblatt und Default. */
  private parameterValues(): { parameterValues?: Record<string, string> } {
    const values = this.parameters()?.values() ?? {};
    return Object.keys(values).length > 0 ? { parameterValues: values } : {};
  }

  private fail(key: StartErrorKey): void {
    this.failWith(errorFor(key));
  }

  private failWith(error: StartError): void {
    this.startError.set(error);
    this.setStarting(false);
  }

  private setStarting(starting: boolean): void {
    this.starting.set(starting);
    this.dialogRef.disableClose = starting;
  }
}

function errorFor(key: StartErrorKey): StartError {
  return { key: `testCases.runDialog.error.${key}` };
}

function errorCode(error: unknown): unknown {
  return error instanceof HttpErrorResponse ? error.error?.code : null;
}

function isRunnerOffline(error: unknown, target: RunTarget): boolean {
  return target.kind === 'runner' && errorCode(error) === 'RUNNER_OFFLINE';
}

/** Absagen des Servers tragen einen Code; ein Modul-Zyklus kommt als 422 nur mit Text. */
function enqueueError(error: unknown, target: RunTarget): StartError {
  const code = errorCode(error);
  if (target.kind === 'runner' && code === 'RUNNER_OFFLINE') {
    return { key: 'testCases.run.runnerOffline', params: { name: target.runnerName } };
  }
  if (target.kind === 'labels' && code === 'NO_MATCHING_RUNNER') {
    return { key: 'testCases.run.noMatchingRunner', params: { labels: target.labels.join(', ') } };
  }
  if (error instanceof HttpErrorResponse && error.status === 422) {
    if (code === 'NO_EXECUTABLE_STEP') {
      return errorFor('noExecutableStep');
    }
    const message: unknown = error.error?.error;
    if (typeof message === 'string' && message.trim()) {
      return { text: message };
    }
  }
  return errorFor(startErrorKey(error));
}

/**
 * Ein Modul hat eigene Texte fuer 403, 404 und „kein Schritt“; Absagen zu Parametern (400, 422)
 * nennen den Namen nur im Text des Servers.
 */
function moduleStartError(error: unknown, target: RunTarget | null): StartError {
  const code = errorCode(error);
  if (target?.kind === 'runner' && code === 'RUNNER_OFFLINE') {
    return { key: 'testCases.run.runnerOffline', params: { name: target.runnerName } };
  }
  if (!(error instanceof HttpErrorResponse)) {
    return errorFor('generic');
  }
  if (error.status === 403) {
    return { key: 'modules.run.error.forbidden' };
  }
  if (error.status === 404) {
    return { key: 'modules.run.error.notFound' };
  }
  if (error.status === 422 && code === 'NO_EXECUTABLE_STEP') {
    return { key: 'modules.run.error.noExecutableStep' };
  }
  const message: unknown = error.error?.error;
  if ((error.status === 400 || error.status === 422) && typeof message === 'string' && message.trim()) {
    return { text: message };
  }
  return errorFor('generic');
}

function startErrorKey(error: unknown): StartErrorKey {
  if (error instanceof HttpErrorResponse && error.status === 403) {
    return 'forbidden';
  }
  if (error instanceof HttpErrorResponse && error.status === 404) {
    return 'notFound';
  }
  return 'generic';
}
